/** Reportes (chat, fan-out, DSA): cola abierta con cierre (resuelto / desestimado) y atajos a usuarios y chat. */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, field, muted, Notice, Table, td, when, str } from '../lib/ui.js';

type Doc = Record<string, unknown>;

export function ReportsPage(): ReactElement {
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    setRows(null);
    getDocs(
      query(
        collection(db, 'reports'),
        where('status', '==', status),
        orderBy('created_at', 'desc'),
        limit(100),
      ),
    )
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [status, version]);
  const close = (id: string, st: 'resolved' | 'dismissed'): void => {
    const note = window.prompt('Nota (opcional):') ?? '';
    call(api.admin.closeReport, { id, status: st, ...(note ? { note } : {}) })
      .then(() => {
        setNotice('Cerrado.');
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  return (
    <>
      <h1 style={{ margin: 0 }}>Reportes</h1>
      <select
        style={{ ...field, width: 'auto' }}
        value={status}
        onChange={(e) => {
          setStatus(e.target.value);
        }}
      >
        {['open', 'resolved', 'dismissed'].map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <Notice text={notice} />
      <Notice text={error} error />
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table head={['Fecha', 'Tipo', 'Motivo', 'Reportado', 'Reporta', 'Conversación', '']}>
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={7}>
                Nada en esta cola.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={String(r.id)}>
              <td style={td}>{when(r.created_at)}</td>
              <td style={td}>{String(r.type)}</td>
              <td style={td}>
                {str(r.reason)}
                {Array.isArray(r.flags) ? ` · ${(r.flags as string[]).join(', ')}` : ''}
                {typeof r.note === 'string' && r.note ? ` · ${r.note}` : ''}
              </td>
              <td style={td}>
                {typeof r.reported_uid === 'string' ? (
                  <Link to={`/usuarios/${r.reported_uid}`}>{r.reported_uid.slice(-8)}</Link>
                ) : (
                  '—'
                )}
              </td>
              <td style={td}>
                {typeof r.reporter_uid === 'string' ? (
                  <Link to={`/usuarios/${r.reporter_uid}`}>{r.reporter_uid.slice(-8)}</Link>
                ) : (
                  'sistema'
                )}
              </td>
              <td style={td}>
                {typeof r.conversation_id === 'string' ? <code>{r.conversation_id}</code> : '—'}
              </td>
              <td style={td}>
                {r.status === 'open' && (
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      style={btn(true)}
                      onClick={() => {
                        close(String(r.id), 'resolved');
                      }}
                    >
                      Resuelto
                    </button>
                    <button
                      type="button"
                      style={btn(false)}
                      onClick={() => {
                        close(String(r.id), 'dismissed');
                      }}
                    >
                      Desestimar
                    </button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
